import { Center, Container, Loader, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import type { WorkPackage } from '@/api/generated/models';
import type { StatusHistoryEntry } from '@/features/history/statusHistoryTypes';
import { WorkPackageStatus } from '@/api/generated/models/workPackageStatus';
import {
  useGetWorkPackage,
  useGetWorkPackageHistory
} from '@/api/generated/work-structure/work-structure';
import { BackButton, ContentCard } from '@/components/common';
import { StatusHistoryTable } from '@/features/history/components/StatusHistoryTable';
import { statusConfig } from '@/features/work-packages/workPackageDisplay';

export const WorkPackageHistoryPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: wpData, isLoading: isLoadingWp } = useGetWorkPackage(id ?? '', {
    query: { enabled: !!id }
  });
  const workPackage = wpData as WorkPackage | undefined;
  const { data: historyData, isLoading: isLoadingHistory } = useGetWorkPackageHistory(id ?? '', {
    query: { enabled: !!id }
  });
  const entries = (historyData as StatusHistoryEntry[] | undefined) ?? [];

  const resolveStatus = (raw: string) => {
    const config = statusConfig[raw as WorkPackageStatus];
    return config
      ? { label: t(config.labelKey), bg: config.bg, fg: config.fg }
      : { label: raw, bg: 'var(--color-bg-surface-2)', fg: 'var(--color-text-secondary)' };
  };

  if (isLoadingWp || isLoadingHistory || !workPackage) {
    return (
      <Container size="lg" px={0}>
        <Center py={80}>
          <Loader size="sm" />
        </Center>
      </Container>
    );
  }

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <BackButton to={`/work-packages/${id}`} />
        <Text fz={14} fw={600} style={{ color: 'var(--color-text-secondary)' }}>
          {workPackage.code} — {workPackage.name}
        </Text>
        <ContentCard>
          <div style={{ padding: 20 }}>
            <StatusHistoryTable entries={entries} resolveStatus={resolveStatus} />
          </div>
        </ContentCard>
      </Stack>
    </Container>
  );
};
